import { useMap as useMapGl, type MapRef } from 'react-map-gl/maplibre';
import { useRef } from 'react';
// @ts-expect-error react-leaflet does not export the Map type
import { useLeaflet } from 'react-leaflet';
import featureFlags from '#/shared/feature-flags';

/**
 * Custom hook that provides a unified map reference for both MapLibre and Leaflet maps.
 * This hook abstracts away the differences between MapLibre and Leaflet useMap and useLeaflet hooks,
 * providing a consistent interface based on the mapLibre feature flag and react-map-gl' useMap interface.
 *
 * @returns {React.RefObject<MapRef>} A reference to the map instance
 * @throws {Error} May throw if used outside of a Map context
 *
 * @example
 * // Inside a React component:
 * function MapComponent() {
 *   const { current: map } = useMap();
 *
 *   useEffect(() => {
 *     if (map) {
 *       // Access map methods directly
 *       map.flyTo({
 *         center: [-122.4, 37.8],
 *         zoom: 14
 *       });
 *     }
 *   }, []);
 *
 *   return <div>Map Component</div>;
 * }
 *
 * @see {@link https://maplibre.org/maplibre-gl-js-docs/api/|MapLibre GL JS API}
 * @see {@link https://leafletjs.com/reference.html|Leaflet API}
 */
function useMap(): React.RefObject<MapRef> {
  const mapRef = useRef<MapRef | null>(null);
  const { map: leafletMap } = useLeaflet(); // useLeaflet returns map instance
  const { current: mapGLMap } = useMapGl(); // useMapGl returns map ref, current is the map instance

  // Update ref value based on the feature flag
  mapRef.current = featureFlags.mapLibre ? mapGLMap : leafletMap;

  return mapRef; // return a ref, consistent with react-map-gl's useMap hook
}

export default useMap;
